<template>
    <explore-widget
        widgetTitle="Incidents"
        :toolTip="dynamicToolTip"
        dataTestBaseString="incident"
        :cardHeight="launchpadWidgetHeights.secondRow"
        :countCard="countCard"
        barChartBtn 
        countBtn
        heatmapBtn
        rangeBtn
        trendBtn
        @exploreClick="incidentClicked"
        @printChart="printChart"
        @exportChart="exportChart"
        @barChartClick="activeTab = 1"
        @countClick="activeTab = 2"
        @heatmapClick="activeTab = 3"
        @trendClick="activeTab = 4"
        @rangeClick="activeTab = 5"
        ref="incidents-explore-widget"
        showFooter
        showMenu
        :showColorBlindMode="activeTab === 3"
        @colorBlindClick="colorBlindMode = $event"
    >
        <template v-slot:widget-body>
            <div v-if="activeTab === 1" ref="barChartContainer">
                <incidents-bar-chart ref="incidents-bar-chart"></incidents-bar-chart>
            </div>
            <div v-else-if="activeTab === 2">
                <div v-if="!countCard.show">
                    <div v-if="countCard.error" style="align-content: center; height: 280px; text-align: center; color: #495B8F;">
                        <v-icon style="font-size: 32px" class="mb-5">fal fa-exclamation-circle</v-icon>
                        <div style="font-weight: 700">Data temporarily unavailable</div>
                        <div>Refresh the page or try again later.</div>
                    </div>
                    <div v-else>
                        <fe-spinner class="spinner"/>
                    </div>
                </div>
            </div>
            <div v-else-if="activeTab === 3" ref="heatmapContainer">
                <calendar-heat-map
                    endpoint="incidentHeatMap"
                    height="300" ref="incidents-heat-map"
                    :colorBlindMode="colorBlindMode"
                    :endpointParams="endpointParams"
                    @heatmapClicked="incidentClicked"
                ></calendar-heat-map>
            </div>
            <div v-else-if="activeTab === 4" ref="trendlineContainer">
                <incidents-trendline ref="incidents-trendline"></incidents-trendline>
            </div>
            <div v-else-if="activeTab === 5" ref="rangeChartContainer">
                <incidents-range-chart ref="incidents-range-chart"></incidents-range-chart>
            </div>
            <div v-else>
                <div style="align-content: center; height: 280px; text-align: center; color: #495B8F;">
                    <v-icon style="font-size: 32px; color: #495B8F;" class="mb-5 incidents-unavailable-icon">fal fa-exclamation-circle</v-icon>
                    <div style="font-weight: 700">Data temporarily unavailable</div>
                    <div>Refresh the page or try again later.</div>
                </div>
            </div>
        </template>
    </explore-widget>
</template>

<script>
import { mapState } from 'vuex'
import ExploreWidget from '../ExploreWidget.vue'
import IncidentsBarChart from './IncidentsBarChart.vue'
import IncidentsRangeChart from './IncidentsRangeChart.vue'
import CalendarHeatMap from '@/components/charts/CalendarHeatMap.vue'
import IncidentsTrendline from './IncidentsTrendline.vue'
import * as ChartExportUtil from '@/components/common/util/ChartExports'

export default {
    name: 'IncidentsCard',

    components: {
        ExploreWidget,
        IncidentsBarChart,
        IncidentsRangeChart,
        CalendarHeatMap,
        IncidentsTrendline,
    },

    computed: {
        ...mapState('global', ['currentYear', 'sessionUser', 'defaultSchool', 'lastLaunchpadVisit', 'launchpadWidgetHeights']),

        hasRestrictedIncidentTypes() {
            return (!!this.sessionUser.user?.has_restricted_incident_types)
        },

        dynamicToolTip() {
            if (this.hasRestrictedIncidentTypes) {
                return 'There are some incident types that have been restricted from your visibility by your district'
            } else {
                return 'Incidents are recorded events involving student behavior or occurrences. Incident data may be imported from your Student Information System (SIS) or entered manually in eduCLIMBER. You can see incident data only for students you have access to.'
            }
        },
    },

    data() {
        return {
            tab: [
                { id: 1, name: 'bar' },
                { id: 2, name: 'count' },
                { id: 3, name: 'heatmap' },
                { id: 4, name: 'range' }
            ],
            activeTab: 1,
            countCard: {
                text: 'Total incidents this school year',
                show: false,
                change: null,
                count: null,
                displayUpDownArrow: null,
                title: 'incidents',
                error: false
            },
            colorBlindMode: false,
            heatmapParams: {
                data: null,
                cohorts: null,
            },
            endpointParams: null,
        }
    },

    watch: {
        activeTab(v) {
            if(v === 2) {
                this.loadCountData()
            } else {
                this.countCard.show = false
                if(v === 3) {
                    this.loadHeatMap()
                }
            }
        }
    },

    methods: {
        incidentClicked(selectedMonthYr = null, e = null) {
            let chartParams = {
                school_year_id: [{id: this.currentYear.id, name: this.currentYear.name}],
            }
            if (this.defaultSchool?.id) chartParams.school_id = [{ id: this.defaultSchool.id, name: this.defaultSchool.name }]

            if(e && selectedMonthYr) {
                let month = this.$dayjs(selectedMonthYr).format('MM')
                let day = e.point.custom.monthDay 
                let yr = this.$dayjs(selectedMonthYr).format('YYYY')
                chartParams.end_date = yr + '-' + month + '-' + day
                chartParams.start_date = yr + '-' + month + '-' + day
            }

            let chartFilters = []
            let location = 'launchpad'
            let params = {chartParams, chartFilters, location}

            this.$router.replace({ name: 'Incident Charting', params })
        },

        async printChart() {
            let container = null
            if(this.$refs.barChartContainer) {
                container = this.$refs.barChartContainer
            } else if(this.$refs.rangeChartContainer) {
                container = this.$refs.rangeChartContainer
            } else if(this.$refs.heatmapContainer) {
                container = this.$refs.heatmapContainer
            } else if(this.$refs.trendlineContainer) {
                container = this.$refs.trendlineContainer
            }
            if(container) {
                await ChartExportUtil.downloadAsPDF(container, { landscape: false, delay: 500 })
            }
        },

        exportChart() {
            if(this.$refs.barChartContainer) {
                ChartExportUtil.exportChartFromNode(this.$refs.barChartContainer, 'Incident Summary Last Two Weeks', this)
            } else if(this.$refs.rangeChartContainer) {
                ChartExportUtil.exportChartFromNode(this.$refs.rangeChartContainer, 'Incident Range', this)
            } else if(this.$refs.heatmapContainer) {
                ChartExportUtil.exportChartFromNode(this.$refs.heatmapContainer, 'Incident Calendar', this)
            } else if(this.$refs.trendlineContainer) {
                ChartExportUtil.exportChartFromNode(this.$refs.trendlineContainer, 'Incident Summary Comparison', this)
            }
        },

        loadCountData() {
            let p = {
                school_id: this.defaultSchool?.id
            }
            this.$axios.get(this.$models.getUrl('incidentSummaryCount', 'read') + '&' + this.$objectToParams(p))
                .then(response => {
                    if(this.activeTab === 2) {
                        if(response.data) {
                            this.countCard = {
                                text: 'Total incidents this school year',
                                show: true,
                                change: response.data.yoy_trend,
                                count: response.data.incident_count,
                                displayUpDownArrow: response.data.yoy_trend < 0 ? 'down' : 'up',
                                title: 'incidents',
                                error: false
                            }
                        } else {
                            this.countCard.show = false
                            this.countCard.error = true
                        }
                    }
                })
        },

        loadHeatMap() {
            let month = parseInt(this.$dayjs().$M) + 1
            if(month < 10) month = '0' + month
            let start = month === '07' ? this.$dayjs().startOf('month').add(1, 'day').format('YYYY-MM-DD') : this.$dayjs().startOf('month').format('YYYY-MM-DD')
            let end = this.$dayjs().endOf('month').format('YYYY-MM-DD')
            let school_id = this.defaultSchool?.id
            this.endpointParams = { start: start, end: end, school_id: school_id }
        }
    }
}
</script>

<style lang="scss" scoped>
.spinner {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    line-height: 250px !important;
}
</style>
